import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageModal, MessageType } from './MessageModal';
import {
  STORAGE_KEYS,
  MARKETPLACE_MESSAGE_TYPES,
  MARKETPLACE_ERROR_TYPES,
  hasUrlParam,
  getUrlParamValue,
  removeUrlParam,
  setStorageFlag,
  hasStorageFlag,
  getStorageFlag,
  removeStorageFlag
} from '@/utils/urlParamManager';

/**
 * Componente PortalRoot
 * 
 * Este componente envolve o conteúdo principal da aplicação e
 * detecta parâmetros especiais na URL, como success=authorized,
 * para exibir mensagens adequadas ao usuário através de um modal.
 * 
 * O modal exigirá que o usuário clique em "OK" para continuar,
 * garantindo que a mensagem seja vista.
 */
export function PortalRoot({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalTitle, setModalTitle] = useState('');
  const [modalType, setModalType] = useState<MessageType>('success');
  
  // Verificar parâmetros na URL ao montar o componente e sempre que a URL mudar
  useEffect(() => {
    // Verificar success=authorized
    if (hasUrlParam('success')) {
      const successType = getUrlParamValue('success');
      
      if (successType === 'authorized') {
        // Salva a flag no sessionStorage
        setStorageFlag(STORAGE_KEYS.MARKETPLACE_SUCCESS);
        
        // Exibe o modal de sucesso
        showMarketplaceModal(
          'success',
          'Fluxpay Ativado!', 
          'Crie uma agenda com o valor da consulta e pronto! Você poderá enviar uma mensagem de cobrança ao paciente agendado e receber o valor das suas consultas diretamente na sua conta PJ do Mercado Pago. Para mais informações consulte o Luxor, ou a documentação do sistema, clicando em Feedback e Suporte.'
        );
        
        // Remove o parâmetro da URL
        removeUrlParam('success', 'authorized');
      }
    } 
    // Verificar parâmetros de erro
    else if (hasUrlParam('error')) {
      const errorType = getUrlParamValue('error');
      
      if (errorType) {
        // Salva o tipo de erro no sessionStorage
        setStorageFlag(STORAGE_KEYS.MARKETPLACE_ERROR, 'true');
        setStorageFlag(STORAGE_KEYS.MARKETPLACE_ERROR_TYPE, errorType);
        
        // Exibe o modal de erro adequado
        showMarketplaceErrorModal(errorType);
        
        // Remove o parâmetro da URL
        removeUrlParam('error', errorType);
      }
    }
    // Verificar flags no sessionStorage
    else {
      // Verificar sucesso no sessionStorage
      if (hasStorageFlag(STORAGE_KEYS.MARKETPLACE_SUCCESS)) {
        showMarketplaceModal(
          'success',
          'FluxPay Ativado!', 
          'Crie uma agenda com o valor da consulta e pronto! Você poderá enviar uma mensagem de cobrança ao paciente agendado e receber o valor das suas consultas diretamente na sua conta PJ do Mercado Pago. Para mais informações consulte o Luxor, ou a documentação do sistema, clicando em Feedback e Suporte.'
        );
        removeStorageFlag(STORAGE_KEYS.MARKETPLACE_SUCCESS);
      } 
      // Verificar erro no sessionStorage
      else if (hasStorageFlag(STORAGE_KEYS.MARKETPLACE_ERROR)) {
        const errorType = getStorageFlag(STORAGE_KEYS.MARKETPLACE_ERROR_TYPE) || 'unknown_error';
        showMarketplaceErrorModal(errorType);
        removeStorageFlag(STORAGE_KEYS.MARKETPLACE_ERROR);
        removeStorageFlag(STORAGE_KEYS.MARKETPLACE_ERROR_TYPE);
      }
    }
  }, [location]);
  
  // Função para exibir o modal com as informações corretas
  const showMarketplaceModal = (type: MessageType, title: string, message: string) => {
    setModalType(type);
    setModalTitle(title);
    setModalMessage(message);
    setModalOpen(true);
  };
  
  // Função para identificar e exibir o modal de erro adequado com base no tipo
  const showMarketplaceErrorModal = (errorType: string) => {
    switch (errorType) {
      case MARKETPLACE_ERROR_TYPES.INVALID_PARAMETERS:
        showMarketplaceModal(
          'error',
          'Parâmetros Inválidos', 
          'Não foi possível processar a autorização do FluxPay devido a parâmetros inválidos. Por favor, tente novamente.'
        );
        break;
        
      case MARKETPLACE_ERROR_TYPES.ALREADY_AUTHORIZED:
        showMarketplaceModal(
          'warning',
          'Autorização Já Existente', 
          'Sua conta já está autorizada no FluxPay. Não é necessário realizar uma nova autorização.'
        );
        break;
        
      case MARKETPLACE_ERROR_TYPES.AUTHORIZATION_FAILED:
        showMarketplaceModal(
          'error',
          'Falha na Autorização', 
          'Houve um erro ao processar sua autorização no FluxPay. Por favor, tente novamente mais tarde ou entre em contato com o suporte.'
        );
        break;
        
      case MARKETPLACE_ERROR_TYPES.UNKNOWN_ERROR:
      default:
        showMarketplaceModal(
          'error',
          'Erro Desconhecido', 
          'Ocorreu um erro inesperado durante o processo de autorização. Por favor, tente novamente mais tarde ou entre em contato com o suporte.'
        );
        break;
    }
  };
  
  // Função para fechar o modal
  const handleCloseModal = () => {
    setModalOpen(false);
    
    // Garante que as flags são removidas ao fechar o modal
    removeStorageFlag(STORAGE_KEYS.MARKETPLACE_SUCCESS);
    removeStorageFlag(STORAGE_KEYS.MARKETPLACE_ERROR);
    removeStorageFlag(STORAGE_KEYS.MARKETPLACE_ERROR_TYPE);
  };

  return (
    <>
      {children}
      
      {/* Modal que será exibido quando necessário */}
      <MessageModal 
        open={modalOpen}
        onClose={handleCloseModal}
        message={modalMessage}
        title={modalTitle}
        type={modalType}
      />
    </>
  );
} 